import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, UserRol } from "../context/AuthContext";
import ErpDashboard from "../components/admin/ErpDashboard";
import ProductCrud from "../components/admin/ProductCrud";
import CategoryCrud from "../components/admin/CategoryCrud";
import SubcategoryCrud from "../components/admin/SubcategoryCrud";
import OrdersList from "../components/admin/OrdersList";
import PosScreen from "../components/admin/PosScreen";
import SalesScreen from "../components/admin/SalesScreen";
import Facturacion from "../components/admin/Facturacion";
import Articulos from "../components/admin/Articulos";
import IvaVentas from "../components/admin/IvaVentas";
import ListasPrecios from "../components/admin/ListasPrecios";
import Proveedores from "../components/admin/Proveedores";
import Clientes from "../components/admin/Clientes";
import StockManager from "../components/admin/StockManager";
import StockFabrica from "../components/admin/StockFabrica";
import RemitoElectronico from "../components/admin/RemitoElectronico";
import Produccion from "../components/admin/Produccion";
import VerificacionRemitos from "../components/admin/VerificacionRemitos";
import PromotionCrud from "../components/admin/PromotionCrud";
import SalesAnalytics from "../components/admin/SalesAnalytics";
import ShippingZones from "../components/admin/ShippingZones";
import VerificacionesCrud from "../components/admin/VerificacionesCrud";
import CajasPanel from "../components/admin/CajasPanel";
import UserManagement from "../components/admin/UserManagement";
import PickingPanel from "../components/admin/PickingPanel";
import NavbarSettings from "../components/admin/NavbarSettings";
import styles from "./styles/AdminPage.module.css";

import {
    FaClipboardList, FaBoxOpen, FaFolder, FaTags, FaSignOutAlt, FaUsers,
    FaCashRegister, FaBoxes, FaGift, FaChartBar, FaMapMarkedAlt,
    FaCheckDouble, FaMoneyBillWave, FaUserShield, FaTruck, FaFileInvoice,
    FaIndustry, FaClipboardCheck, FaListOl, FaThLarge, FaFileInvoiceDollar,
    FaExchangeAlt, FaDollarSign, FaShoppingCart, FaAddressBook, FaTruckLoading,
    FaQrcode, FaSearch, FaBell, FaChevronDown
} from "react-icons/fa";

/* --------------------------------------------------------------------------
 * Secciones del ERP.
 * El bloque nuevo (dashboard -> etiquetas) es el foco del rediseno; el bloque
 * "legacy" agrupa las pantallas del panel anterior que siguen operativas.
 * ------------------------------------------------------------------------ */

type TabKey =
    // ERP
    // "Tomar pedido" ya no existe: el punto de venta absorbio la carga de
    // pedidos de retiro y envio (ver PosScreen).
    | "dashboard" | "pos" | "facturacion" | "notas" | "listas-precios"
    | "ventas" | "iva-ventas" | "articulos" | "clientes" | "proveedores" | "etiquetas"
    // Legacy
    | "products" | "categories" | "subcategories" | "orders"
    | "stock" | "promotions" | "stats" | "zonas" | "verificaciones"
    | "caja" | "stockfabrica" | "usuarios"
    | "picking" | "remito" | "produccion" | "verificacion-remitos" | "navbar";

const TAB_ACCESS: Record<TabKey, UserRol[]> = {
    // ERP
    dashboard:       ["admin", "vendedor", "cocinero", "fabrica", "it", "contabilidad"],
    pos:             ["admin", "vendedor", "fabrica"],
    facturacion:     ["admin", "vendedor", "contabilidad"],
    notas:           ["admin", "contabilidad"],
    "iva-ventas":    ["admin", "contabilidad"],
    "listas-precios":["admin", "contabilidad"],
    ventas:          ["admin", "vendedor", "cocinero", "fabrica", "contabilidad"],
    articulos:       ["admin"],
    clientes:        ["admin", "vendedor", "fabrica"],
    proveedores:     ["admin", "contabilidad"],
    etiquetas:       ["admin", "vendedor", "fabrica"],
    // Legacy
    orders:          ["admin", "vendedor", "cocinero", "fabrica", "contabilidad"],
    products:        ["admin"],
    promotions:      ["admin"],
    categories:      ["admin"],
    subcategories:   ["admin"],
    stock:           ["admin", "vendedor", "cocinero", "fabrica", "it"],
    stockfabrica:    ["admin", "cocinero", "fabrica"],
    stats:           ["admin", "it", "contabilidad"],
    zonas:           ["admin", "vendedor", "fabrica"],
    verificaciones:  ["admin", "it", "contabilidad"],
    picking:         ["admin", "fabrica"],
    caja:            ["admin", "vendedor", "fabrica", "contabilidad"],
    usuarios:        ["admin", "it"],
    navbar:          ["admin"],
    remito:                 ["admin", "vendedor", "cocinero", "fabrica"],
    produccion:             ["admin", "cocinero", "fabrica"],
    "verificacion-remitos": ["admin", "vendedor", "cocinero", "fabrica", "it"],
};

const TAB_TITLES: Record<TabKey, string> = {
    dashboard:        "Panel general",
    pos:              "POS",
    facturacion:      "Facturación",
    notas:            "Notas de débito y crédito",
    "iva-ventas":     "IVA Ventas",
    "listas-precios": "Listas de precios",
    ventas:           "Ventas",
    articulos:        "Artículos",
    clientes:         "Clientes",
    proveedores:      "Proveedores",
    etiquetas:        "Etiquetas de artículos",
    orders:                 "Gestión de pedidos",
    products:               "Gestión de productos",
    promotions:             "Promociones",
    categories:             "Gestión de categorías",
    subcategories:          "Gestión de subcategorías",
    stock:                  "Gestión de stock",
    remito:                 "Remito electrónico",
    "verificacion-remitos": "Verificación de remitos",
    produccion:             "Producción",
    stats:                  "Datos de ventas",
    zonas:                  "Zonas de envío",
    verificaciones:         "Verificaciones de pago",
    caja:                   "Cajas diarias",
    stockfabrica:           "Stock materia prima",
    picking:                "Preparación de envíos",
    usuarios:               "Gestión de usuarios",
    navbar:                 "Configuración del navbar",
};

const ROL_LABELS: Record<UserRol, string> = {
    admin:        "Admin",
    vendedor:     "Vendedor",
    cocinero:     "Cocinero",
    fabrica:      "Fábrica",
    it:           "IT",
    contabilidad: "Contabilidad",
};

interface NavItem {
    tab: TabKey;
    icon: React.ReactNode;
    label: string;
}

interface NavGroup {
    title: string | null;
    items: NavItem[];
    /** Los grupos colapsables arrancan cerrados (se usa para "legacy"). */
    collapsible?: boolean;
}

const NAV_GROUPS: NavGroup[] = [
    {
        title: null,
        items: [
            { tab: "dashboard", icon: <FaThLarge />, label: "Panel general" },
            { tab: "pos", icon: <FaCashRegister />, label: "POS" },
        ],
    },
    {
        title: "Comprobantes",
        items: [
            { tab: "facturacion", icon: <FaFileInvoiceDollar />, label: "Facturación" },
            { tab: "notas", icon: <FaExchangeAlt />, label: "Notas D/C" },
            { tab: "ventas", icon: <FaShoppingCart />, label: "Ventas" },
            { tab: "iva-ventas", icon: <FaFileInvoice />, label: "IVA Ventas" },
        ],
    },
    {
        title: "Catálogo",
        items: [
            { tab: "articulos", icon: <FaBoxOpen />, label: "Artículos" },
            { tab: "listas-precios", icon: <FaDollarSign />, label: "Listas de precios" },
            { tab: "etiquetas", icon: <FaQrcode />, label: "Etiquetas" },
        ],
    },
    {
        title: "Terceros",
        items: [
            { tab: "clientes", icon: <FaAddressBook />, label: "Clientes" },
            { tab: "proveedores", icon: <FaTruckLoading />, label: "Proveedores" },
        ],
    },
    {
        title: "Operación (panel anterior)",
        collapsible: true,
        items: [
            { tab: "orders", icon: <FaClipboardList />, label: "Pedidos" },
            { tab: "products", icon: <FaBoxOpen />, label: "Productos" },
            { tab: "promotions", icon: <FaGift />, label: "Promociones" },
            { tab: "categories", icon: <FaFolder />, label: "Categorías" },
            { tab: "subcategories", icon: <FaTags />, label: "Subcategorías" },
            { tab: "stock", icon: <FaBoxes />, label: "Stock" },
            { tab: "remito", icon: <FaFileInvoice />, label: "Remito electrónico" },
            { tab: "verificacion-remitos", icon: <FaClipboardCheck />, label: "Verificación remitos" },
            { tab: "produccion", icon: <FaIndustry />, label: "Producción" },
            { tab: "stockfabrica", icon: <FaBoxes />, label: "Stock fábrica" },
            { tab: "stats", icon: <FaChartBar />, label: "Analíticas" },
            { tab: "zonas", icon: <FaMapMarkedAlt />, label: "Zonas de envío" },
            { tab: "verificaciones", icon: <FaCheckDouble />, label: "Verificaciones" },
            { tab: "picking", icon: <FaTruck />, label: "Preparación envíos" },
            { tab: "caja", icon: <FaMoneyBillWave />, label: "Cajas" },
            { tab: "usuarios", icon: <FaUserShield />, label: "Usuarios" },
            { tab: "navbar", icon: <FaListOl />, label: "Navbar del sitio" },
        ],
    },
];

/** Pantalla puente para las secciones del ERP todavia sin implementar. */
const ComingSoon: React.FC<{ title: string; detail: string }> = ({ title, detail }) => (
    <div className={styles.placeholder}>
        <h3>{title}</h3>
        <p>{detail}</p>
    </div>
);

const AdminPage: React.FC = () => {
    const { isAuthenticated, logout, user, rol } = useAuth();
    const userRol: UserRol = (rol as UserRol) || "admin";
    const [activeTab, setActiveTab] = useState<TabKey>("dashboard");
    const [legacyOpen, setLegacyOpen] = useState(false);

    const canAccess = React.useCallback(
        (tab: TabKey) => TAB_ACCESS[tab].includes(userRol),
        [userRol],
    );

    // Se ocultan los grupos cuyos items estan todos fuera del alcance del rol.
    const visibleGroups = useMemo(
        () =>
            NAV_GROUPS
                .map(group => ({ ...group, items: group.items.filter(item => canAccess(item.tab)) }))
                .filter(group => group.items.length > 0),
        [canAccess],
    );

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    const initials = (user || "AD").slice(0, 2).toUpperCase();

    return (
        <div className={styles.shell}>
            <aside className={styles.sidebar}>
                <div className={styles.brand}>
                    <span className={styles.brandMark}>A</span>
                    <div className={styles.brandText}>
                        <strong>Ardel</strong>
                        <span>Gestión comercial</span>
                    </div>
                </div>

                <nav className={styles.nav}>
                    {visibleGroups.map(group => {
                        const isCollapsible = !!group.collapsible;
                        const isOpen = !isCollapsible || legacyOpen;

                        return (
                            <div key={group.title ?? "root"} className={styles.navGroup}>
                                {group.title && (
                                    isCollapsible ? (
                                        <button
                                            type="button"
                                            className={styles.groupToggle}
                                            onClick={() => setLegacyOpen(open => !open)}
                                            aria-expanded={isOpen}
                                        >
                                            {group.title}
                                            <FaChevronDown
                                                className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
                                            />
                                        </button>
                                    ) : (
                                        <span className={styles.groupTitle}>{group.title}</span>
                                    )
                                )}

                                {isOpen && group.items.map(item => (
                                    <button
                                        key={item.tab}
                                        type="button"
                                        className={`${styles.navBtn} ${activeTab === item.tab ? styles.navBtnActive : ""}`}
                                        onClick={() => setActiveTab(item.tab)}
                                    >
                                        <span className={styles.navIcon}>{item.icon}</span>
                                        <span className={styles.navLabel} data-label={item.label}>{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </nav>

                <button type="button" onClick={logout} className={styles.logoutBtn}>
                    <FaSignOutAlt /> Cerrar sesión
                </button>
            </aside>

            <div className={styles.main}>
                <header className={styles.topbar}>
                    <h1 className={styles.topbarTitle}>{TAB_TITLES[activeTab]}</h1>

                    <div className={styles.search}>
                        <FaSearch className={styles.searchIcon} />
                        <input
                            type="search"
                            placeholder="Buscar artículos, clientes o comprobantes…"
                            aria-label="Buscar"
                        />
                    </div>

                    <div className={styles.topbarRight}>
                        <button type="button" className={styles.iconBtn} aria-label="Notificaciones">
                            <FaBell />
                        </button>
                        <div className={styles.userChip}>
                            <span className={styles.avatar}>{initials}</span>
                            <div className={styles.userMeta}>
                                <strong>{user || "Admin"}</strong>
                                <span>{ROL_LABELS[userRol]}</span>
                            </div>
                        </div>
                    </div>
                </header>

                <main className={styles.content}>
                    {activeTab === "dashboard" && <ErpDashboard />}

                    {activeTab === "facturacion" && canAccess("facturacion") && <Facturacion />}

                    {/* --- ERP: pantallas pendientes de implementar --- */}
                    {activeTab === "notas" && canAccess("notas") && (
                        <ComingSoon
                            title="Notas de débito y crédito"
                            detail="Emisión de notas asociadas a comprobantes existentes."
                        />
                    )}
                    {activeTab === "listas-precios" && canAccess("listas-precios") && <ListasPrecios />}
                    {activeTab === "ventas" && canAccess("ventas") && <SalesScreen />}
                    {activeTab === "iva-ventas" && canAccess("iva-ventas") && <IvaVentas />}
                    {activeTab === "articulos" && canAccess("articulos") && <Articulos />}
                    {activeTab === "clientes" && canAccess("clientes") && <Clientes />}
                    {activeTab === "proveedores" && canAccess("proveedores") && <Proveedores />}
                    {activeTab === "etiquetas" && canAccess("etiquetas") && (
                        <ComingSoon
                            title="Etiquetas de artículos"
                            detail="Generación e impresión de etiquetas con código de barras."
                        />
                    )}

                    {/* --- Pantallas operativas ya existentes --- */}
                    {/* Tras cobrar, el operador se queda en el mostrador para la venta siguiente. */}
                    {activeTab === "pos"            && canAccess("pos")            && <PosScreen />}
                    {activeTab === "orders"         && canAccess("orders")         && <OrdersList />}
                    {activeTab === "products"       && canAccess("products")       && <ProductCrud />}
                    {activeTab === "promotions"     && canAccess("promotions")     && <PromotionCrud />}
                    {activeTab === "categories"     && canAccess("categories")     && <CategoryCrud />}
                    {activeTab === "subcategories"  && canAccess("subcategories")  && <SubcategoryCrud />}
                    {activeTab === "stock"          && canAccess("stock")          && <StockManager />}
                    {activeTab === "remito"               && canAccess("remito")               && <RemitoElectronico />}
                    {activeTab === "verificacion-remitos" && canAccess("verificacion-remitos") && <VerificacionRemitos />}
                    {activeTab === "produccion"           && canAccess("produccion")           && <Produccion />}
                    {activeTab === "stats"          && canAccess("stats")          && <SalesAnalytics />}
                    {activeTab === "zonas"          && canAccess("zonas")          && <ShippingZones />}
                    {activeTab === "verificaciones" && canAccess("verificaciones") && <VerificacionesCrud />}
                    {activeTab === "caja"           && canAccess("caja")           && <CajasPanel />}
                    {activeTab === "stockfabrica"   && canAccess("stockfabrica")   && <StockFabrica />}
                    {activeTab === "picking"        && canAccess("picking")        && <PickingPanel />}
                    {activeTab === "usuarios"       && canAccess("usuarios")       && <UserManagement />}
                    {activeTab === "navbar"         && canAccess("navbar")         && <NavbarSettings />}
                </main>
            </div>
        </div>
    );
};

export default AdminPage;
